/**
 * DemoTour — the per-demo guided walkthrough for prospect (demo) sessions.
 *
 * Steps come from `me.demo.tour.steps` (set per demo by the ops plane / MCP).
 * There is NO built-in fallback tour: a demo that defines no steps shows no
 * tour at all (the ops plane refuses to hand out credentials for such demos,
 * so a prospect never lands in one). Each step may point at a stable chrome anchor
 * (`[data-tour=...]` CSS selector) — the overlay spotlights it with the
 * fixed-backdrop-with-cutout technique (a highlight box whose giant
 * `box-shadow` paints the scrim, so the target stays at full brightness
 * inside the hole). The target is scrolled into view first, and the box is
 * clipped to the window — a target taller than the viewport (the canvas
 * sheet always is) would otherwise push the scrim shadow off-screen and
 * dim nothing. Steps with no/missing target render as a centered card.
 *
 * Multi-page steps: a step may also carry a `path` (app-relative route). On
 * entering it — forward, back, or dot-jump — the tour navigates there unless
 * the current pathname IS that route (./tourPath.ts), then waits up to
 * NAV_WAIT_MS for the target to appear ("Taking you there…" on the centered
 * card) before settling for the centered fallback. Navigation is attempted
 * at most once per step entry, so a route-guard bounce can never loop; Esc
 * always exits, and finishing/skipping the tour never navigates anywhere —
 * the prospect stays wherever the tour left them.
 *
 * MOUNT POINT: routes/__root.tsx (via DemoTourChrome), ABOVE both the `_app`
 * layout and /manage — required, because tours cross between them and an
 * overlay mounted inside `_app` would unmount mid-walkthrough.
 *
 * Auto-starts on entry until finished or skipped (localStorage
 * `chai-demo-tour-seen:<subdomain>`, written on dismiss/complete; auto-launch
 * capped at 3 attempts); relaunches any time via the sidebar's
 * "Show me around" affordance (a `chaidojo:start-tour` window event).
 * Keyboard: Esc skips, arrows navigate, Enter advances.
 *
 * ONE NARRATIVE, TWO SURFACES. When a demo also carries a first-run rail, the
 * tour STARTS ON ITS CHAPTER MENU instead of marching through the intro
 * screens: the rail has already said what the product is, whose data this is,
 * and what was loaded, so repeating it is the fastest way to make a prospect
 * stop reading. The tour's job becomes à-la-carte — pick the chapter you want.
 * Demos with no rail keep the intro march exactly as it was, which is what
 * preserves the platform's product-intro guarantee for them.
 *
 * LOOK: DARK (./chromeTheme.ts), like every other piece of demo chrome, and
 * badged "DEMO GUIDE". The spotlight already dims the app; the card now also
 * READS as a different kind of object from the app it is pointing at.
 */

import { useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import type { DemoMeBrief } from "@chai/shared";

import { postDemoEvent } from "@/lib/demoEvents";

import {
  DEMO_BADGE,
  DEMO_CHROME,
  DEMO_EYEBROW,
  DEMO_GHOST_BTN,
  DEMO_PANEL,
  DEMO_PRIMARY_BTN,
  DEMO_SERIF_BODY,
  DEMO_SUBTLE_BTN,
  demoChromeVars,
} from "./chromeTheme";
import { parseFirstRunPayload } from "./firstRun/payload";
import { firstRunPlayedThisSession, isFirstRunSeen } from "./firstRun/seen";
import { announceGuideOpened, onGuideOpened, setGuideOpen } from "./guideOverlays";
import { isOnStepPath } from "./tourPath";

export type DemoTourStep = {
  target?: string;
  /** App-relative route ("/plan/all-work") to open on step entry. Satisfied
   * only by being ON that exact page (./tourPath.ts) — a canvas under
   * "/w/<slug>" does not count as the workspace home. Empty/unset -> never
   * navigate.
   *
   * Tokens: "@workspace", "@canvas" and "@artifact" name the demo's HERO
   * (the landing canvas's workspace home, the landing canvas, the first
   * deliverable on it). The demo platform resolves them to stable routes
   * before the tour reaches /me, so normally they arrive here as plain
   * paths. One still arriving as a token (a config pushed before its hero
   * was seeded) falls back to the runtime lookup in resolveStepPath.
   * "@plan-project" is always resolved here: the first project on the
   * all-work board ([data-tour=plan-board]), so order a "/plan/all-work"
   * step before it. An unresolvable token never navigates — the step
   * simply renders wherever the user already is. */
  path?: string;
  /** Section name. When ANY step carries a section, the tour runs in
   * HIERARCHICAL mode: a section menu opens first, each section is a short
   * 3–5 step walk that returns to the menu, and finished sections get a
   * check. A leading sectionless step becomes the menu's welcome text.
   * No sections at all -> the classic linear walkthrough. */
  section?: string;
  title: string;
  body: string;
  /** Custom label for this step's advance button (default "Next") — e.g.
   * "Start the demo — about 5 minutes" on the last intro screen. */
  next_label?: string;
};

// Sentinel href shapes: a canvas page is `/w/{slug}/c/{canvasId}`; an
// artifact page nests `/a/{artifactId}` under it. The canvas pattern is
// anchored at the end so artifact links (which share the `/c/` prefix)
// never satisfy "@canvas". A Plan project home is `/plan/p/{projectSlug}`
// (the all-work board's project cards and the Plan rail are real links).
const CANVAS_HREF = /^\/w\/[^/]+\/c\/[^/]+\/?$/;
const ARTIFACT_HREF = /^\/w\/[^/]+\/c\/[^/]+\/a\/[^/]+\/?$/;
const PROJECT_HREF = /^\/plan\/p\/[^/]+\/?$/;

/** First app link under `hrefPrefix` whose href matches `pattern`,
 * preferring links inside `scope` (the stable data-tour container) and
 * falling back to anywhere in the document. The fallback is what lets
 * "@artifact" resolve while the user is on a canvas page: its artifacts
 * list renders the same links without the overview's [data-tour=artifacts]
 * wrapper (and "@plan-project" fall back to the Plan rail's project
 * links when the board isn't mounted). */
function findTourLink(scope: string, hrefPrefix: string, pattern: RegExp): string | null {
  for (const prefix of [`${scope} `, ""]) {
    const links = document.querySelectorAll<HTMLAnchorElement>(
      `${prefix}a[href^="${hrefPrefix}"]`,
    );
    for (const link of links) {
      const href = link.getAttribute("href") ?? "";
      if (pattern.test(href)) return href;
    }
  }
  return null;
}

/** Resolve a step's path, expanding dynamic sentinels. Returns null when
 * there is nothing to navigate to (no path, or a sentinel that can't be
 * resolved on the current page — e.g. "@workspace" while the app sidebar
 * isn't mounted); the step then renders wherever the user already is.
 *
 * "@workspace" / "@canvas" / "@artifact" only get this far when the demo
 * platform could not resolve them to the hero's routes (a config pushed
 * before the hero was seeded): the link lookups below are that fallback,
 * and since the lists they read are newest-first they can land on a
 * baked run's output — which is why the platform resolves them first. */
function resolveStepPath(raw: string | undefined): string | null {
  const path = (raw ?? "").trim();
  if (!path) return null;
  if (path === "@workspace") {
    const link = document.querySelector<HTMLAnchorElement>(
      '[data-tour=workspaces] a[href^="/w/"]',
    );
    if (link?.getAttribute("href")) return link.getAttribute("href");
    // The sidebar's workspace nav only renders in ACT mode, so this
    // sentinel resolved to null on every Plan page — and a tour that
    // walks Plan BEFORE the workspace (the golden brief's own order:
    // /plan/all-work -> @plan-project -> @workspace) therefore always
    // stalled here, advertising "Taking you there…" and going nowhere.
    // Fall back to any workspace link on the page, then to the workspace
    // directory: /workspaces always exists, and landing there puts the
    // prospect back in Act mode so the "@canvas"/"@artifact" steps that
    // follow can resolve at all.
    const anyWs = document.querySelector<HTMLAnchorElement>('a[href^="/w/"]');
    return anyWs?.getAttribute("href") || "/workspaces";
  }
  if (path === "@canvas") {
    // Prefer the parent canvas of the FIRST ARTIFACT link (the exact
    // lookup "@artifact" uses): the tour walks canvas → artifact, and the
    // first canvas in the grid may own none of the workspace's artifacts
    // (BSC's tour opened "Portfolio Map" while the deck lived on the IC
    // Paper). Deriving the canvas from the artifact guarantees the
    // canvas steps and the artifact steps happen on the SAME document.
    // Workspaces with canvases but no artifacts fall back to the first
    // canvas link, as before.
    const artifact = findTourLink("[data-tour=artifacts]", "/w/", ARTIFACT_HREF);
    const parent = artifact?.replace(/\/a\/[^/]+\/?$/, "") ?? null;
    if (parent && CANVAS_HREF.test(parent)) return parent;
    return findTourLink("[data-tour=canvases]", "/w/", CANVAS_HREF);
  }
  if (path === "@artifact") {
    return findTourLink("[data-tour=artifacts]", "/w/", ARTIFACT_HREF);
  }
  if (path === "@plan-project") {
    return findTourLink("[data-tour=plan-board]", "/plan/p/", PROJECT_HREF);
  }
  return path;
}

const TOUR_EVENT = "chaidojo:start-tour";
const seenKey = (subdomain: string, userKey: string) =>
  `chai-demo-tour-seen:${subdomain || "demo"}:${userKey || "anon"}`;

/** How long a freshly-entered step waits for its target to exist (route
 * transition, lazy panes, org-context loads) before the centered fallback
 * stops advertising "Taking you there…". The rect poll keeps running after —
 * a slower-than-budget target still gets its spotlight when it lands. */
const NAV_WAIT_MS = 4000;

/** Relaunch the tour from anywhere (sidebar affordance, help menus). */
export function startDemoTour(): void {
  window.dispatchEvent(new CustomEvent(TOUR_EVENT));
}

// ── geometry ────────────────────────────────────────────────────────────────

const SPOT_PAD = 6; // px of breathing room around the spotlit target
const CARD_W = 420;
const CARD_GAP = 12;
const EDGE = 16;
const CARD_EST_H = 270; // placement estimate only — CSS does the real math

type Rect = { top: number; left: number; width: number; height: number };

function measureTarget(selector: string | undefined): Rect | null {
  if (!selector) return null;
  let el: Element | null = null;
  try {
    el = document.querySelector(selector);
  } catch {
    return null; // user-authored selector may be invalid CSS
  }
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width < 2 || r.height < 2) return null; // hidden / collapsed
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

function sameRect(a: Rect | null, b: Rect | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    Math.abs(a.top - b.top) < 1 &&
    Math.abs(a.left - b.left) < 1 &&
    Math.abs(a.width - b.width) < 1 &&
    Math.abs(a.height - b.height) < 1
  );
}

/** Live viewport rect of the step's target: re-measured on resize, scroll
 * (capture — inner panes scroll), and a slow interval so async layout and SPA
 * navigations are caught without a MutationObserver. Returns the rect plus a
 * `remeasure()` the caller fires after it moves the target itself (a
 * programmatic scroll), so the spotlight never paints a frame at the stale
 * pre-scroll position. */
function useTargetRect(selector: string | undefined, active: boolean): [Rect | null, () => void] {
  const [rect, setRect] = useState<Rect | null>(null);
  // Holds the live measure closure so `remeasure` can stay identity-stable
  // (it goes in an effect's dep array downstream).
  const measureRef = useRef<() => void>(() => {});
  // Viewport the current rect was reconciled against. The spotlight clamps to
  // the window, so a resize has to publish a new rect even when the target's
  // own rect is unchanged — dragging just the window's bottom edge does
  // exactly that to a fixed-width sheet.
  const viewRef = useRef({ w: 0, h: 0 });
  useEffect(() => {
    if (!active) {
      measureRef.current = () => {};
      setRect(null);
      return;
    }
    const measure = () => {
      const next = measureTarget(selector);
      const view = { w: window.innerWidth, h: window.innerHeight };
      const resized = view.w !== viewRef.current.w || view.h !== viewRef.current.h;
      viewRef.current = view;
      setRect((prev) => (!resized && sameRect(prev, next) ? prev : next));
    };
    measureRef.current = measure;
    measure();
    const interval = window.setInterval(measure, 250);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      measureRef.current = () => {};
      window.clearInterval(interval);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [selector, active]);
  const remeasure = useCallback(() => measureRef.current(), []);
  return [rect, remeasure];
}

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), Math.max(lo, hi));

/** Gap kept between a CLIPPED spotlight edge and the window edge, so the ring
 * on that side stays on screen. */
const CLIP_INSET = 10;

/** The on-screen slice of the target — the box the spotlight actually paints.
 *
 * The cutout technique only works while the hole has edges inside the window:
 * the scrim is the box's own `0 0 0 9999px` shadow, so a box taller than the
 * viewport pushes that shadow entirely off-screen and the prospect gets no
 * dimming and no visible ring — just two vertical lines running off both
 * ends. The canvas sheet ([data-tour=canvas-page]) is routinely three or four
 * screens tall, so this is the common case, not the edge case.
 *
 * Only edges that actually fall outside the window move; a target that fits
 * keeps its exact rect, because a spotlight has to sit ON its element rather
 * than near it. Null when the target is scrolled fully out of view — the
 * caller then falls back to the plain scrim and the centered card. */
function visibleRect(rect: Rect | null): Rect | null {
  if (!rect) return null;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const top = rect.top < 0 ? CLIP_INSET : rect.top;
  const left = rect.left < 0 ? CLIP_INSET : rect.left;
  const bottom = rect.top + rect.height > vh ? vh - CLIP_INSET : rect.top + rect.height;
  const right = rect.left + rect.width > vw ? vw - CLIP_INSET : rect.left + rect.width;
  if (bottom - top < 2 || right - left < 2) return null;
  return { top, left, width: right - left, height: bottom - top };
}

/** Card position near the target: beside tall rail-like targets when there's
 * room, otherwise below (or above when the bottom is tight), always clamped
 * into the viewport. Above-placement uses translateY(-100%) so we never need
 * the card's real height. */
function cardStyleFor(rect: Rect | null, cardH: number = CARD_EST_H): React.CSSProperties {
  if (!rect) {
    return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  }
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const right = rect.left + rect.width;
  const bottom = rect.top + rect.height;

  // Tall targets (sidebar nav, agent rail): sit beside them.
  if (rect.height > 220) {
    const top = clamp(rect.top + 16, EDGE, Math.max(EDGE, vh - cardH - EDGE));
    if (right + CARD_GAP + CARD_W <= vw - EDGE) {
      return { top, left: right + CARD_GAP };
    }
    if (rect.left - CARD_GAP - CARD_W >= EDGE) {
      return { top, left: rect.left - CARD_GAP - CARD_W };
    }
  }

  const left = clamp(
    rect.left + rect.width / 2 - CARD_W / 2,
    EDGE,
    Math.max(EDGE, vw - CARD_W - EDGE),
  );
  const fitsBelow = bottom + CARD_GAP + cardH <= vh - EDGE;
  const fitsAbove = rect.top - CARD_GAP - cardH >= EDGE;
  if (!fitsBelow && fitsAbove) {
    return { top: rect.top - CARD_GAP, left, transform: "translateY(-100%)" };
  }
  // Clamp by the REAL height so the footer (Next) never runs off-screen —
  // long multi-paragraph bodies exceed any fixed estimate.
  return { top: clamp(bottom + CARD_GAP, EDGE, Math.max(EDGE, vh - cardH - EDGE)), left };
}

// ── component ───────────────────────────────────────────────────────────────

export function DemoTour({
  demo,
  userKey = "",
  firstName = "",
}: {
  demo: DemoMeBrief;
  userKey?: string;
  /** Signed-in user's first name — steps may greet with a {first_name}
   * placeholder ("Welcome, {first_name}."), rendered client-side. */
  firstName?: string;
}) {
  const personalize = useCallback(
    (s: string) => s.replaceAll("{first_name}", firstName.trim() || "there"),
    [firstName],
  );
  // Custom steps only — there is deliberately NO built-in fallback tour:
  // a demo without an authored walkthrough shows no tour at all (the mount
  // sites also gate on this; the guard below is defense in depth).
  const allSteps: DemoTourStep[] = useMemo(() => demo.tour?.steps ?? [], [demo.tour]);

  // Hierarchical mode: any sectioned step turns the tour into a chapter menu
  // + short per-chapter walks. A leading sectionless step is the menu's
  // welcome text; chapters keep first-appearance order.
  // EVERY leading sectionless step is an intro screen, played once as a short
  // paged sequence before the section menu. One screen is the common case (the
  // demo-specific "here is what this review is"); a product-level "what
  // Chaidojo is, who it's for, what it does" screen can sit in front of it.
  const introSteps = useMemo(() => {
    const out: DemoTourStep[] = [];
    for (const st of allSteps) {
      if ((st.section ?? "").trim()) break;
      out.push(st);
    }
    return out;
  }, [allSteps]);
  // The demo-specific screen — titles the menu and the dialog's aria-label.
  const intro = introSteps.length > 0 ? (introSteps[introSteps.length - 1] ?? null) : null;
  const chapters = useMemo(() => {
    const names: string[] = [];
    for (const s of allSteps) {
      const sec = (s.section ?? "").trim();
      if (sec && !names.includes(sec)) names.push(sec);
    }
    return names;
  }, [allSteps]);
  const hierarchical = chapters.length > 0;

  const [active, setActive] = useState(false);
  const [view, setView] = useState<"intro" | "menu" | "steps">("steps");
  const [introIdx, setIntroIdx] = useState(0);
  const [chapter, setChapter] = useState<string | null>(null);
  const [doneChapters, setDoneChapters] = useState<string[]>([]);
  const [idx, setIdx] = useState(0);

  // The steps the walk actually iterates: the active chapter's steps in
  // hierarchical mode, the whole list in classic linear mode.
  const steps: DemoTourStep[] = useMemo(() => {
    if (!hierarchical) return allSteps;
    return allSteps.filter((s) => (s.section ?? "").trim() === chapter);
  }, [allSteps, hierarchical, chapter]);
  // True while a just-entered step is still waiting for its target (usually
  // because we navigated and the page is loading) — drives the "Taking you
  // there…" hint on the centered card.
  const [waitingForTarget, setWaitingForTarget] = useState(false);
  // Real rendered card height (estimate until measured) — placement clamps
  // with this so the footer never clips below the viewport on long bodies.
  const cardRef = useRef<HTMLDialogElement | null>(null);
  const [cardH, setCardH] = useState(CARD_EST_H);
  useEffect(() => {
    const measure = () => {
      const h = cardRef.current?.getBoundingClientRect().height;
      if (h && Math.abs(h - cardH) > 1) setCardH(h);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  });
  const router = useRouter();
  const storageKey = seenKey(demo.subdomain, userKey);
  // Walkthrough-led demos reopen the tour on EVERY login: the seen-mark (and
  // the auto-start attempt counter) live in sessionStorage instead, so a new
  // browser session starts fresh while refreshes inside one stay quiet.
  const everyLogin = Boolean(demo.tour?.show_every_login);
  const seenStore = useCallback(
    (): Storage => (everyLogin ? window.sessionStorage : window.localStorage),
    [everyLogin],
  );
  // True once the walk actually completed (vs. skipped) — decides which
  // engagement beacon the dismiss fires.
  const completedRef = useRef(false);

  // The first-run rail owns the SESSION it plays in, not just the first
  // paint: while it is pending, and for the rest of any session it showed
  // in, the tour's AUTO-start stands down. Releasing it the moment the rail
  // closed (the old FIRST_RUN_DONE_EVENT wiring) stacked the two overlays
  // back to back — the tour auto-opened over the deliverable the reveal had
  // just landed on. Freezing a mount-time value didn't survive either: this
  // chrome unmounts and remounts across route-level data refetches, and the
  // remount re-read only the persistent seen-mark (filmed: the tour opened
  // over the deck artifact minutes after the rail closed). So the read is
  // computed, and "played this session" is a sessionStorage mark that
  // outlives remounts and dies with the tab — the NEXT login auto-starts as
  // normal, and the rail's "See how it's made" and the sidebar's "Take the
  // tour" launch explicitly at any time.
  /** Does this demo have a first-run rail at all? (Not "is one showing" — see
   *  `railPending` below.) When it does, the rail owns the orientation and the
   *  tour opens on its chapter menu instead of re-marching the intro. */
  const hasRail = parseFirstRunPayload(demo.first_run) !== null;
  // `demo.first_run_seen` (the SERVER's per-seat mark) matters here as much
  // as in the rail's own gate: without it, a prospect opening the demo on a
  // second machine — where localStorage knows nothing but the server knows
  // the arc is done — had railPending stuck true forever, which suppressed
  // the tour's auto-start too. Net effect: second device, NEITHER overlay
  // (hit live in an audit). One argument fixes it.
  const railPending =
    hasRail &&
    (!isFirstRunSeen(demo.subdomain, userKey, demo.first_run_seen) ||
      firstRunPlayedThisSession(demo.subdomain, userKey));

  /** The view a start (auto or manual) opens on.
   *
   *  A sectioned tour normally plays its leading sectionless steps as an intro
   *  march and then shows the menu. With a rail in the demo those screens are
   *  the third telling of the same story — the rail's `what` beat says what
   *  Chaidojo is, and the tour used to say it again over a scrim — so the menu
   *  opens directly. A tour with no sections has no menu to open, and a demo
   *  with no rail keeps the march (that is where the platform's product-intro
   *  screen guarantee lives). */
  const startView = useCallback(
    (): "intro" | "menu" | "steps" =>
      hierarchical ? (introSteps.length > 0 && !hasRail ? "intro" : "menu") : "steps",
    [hierarchical, introSteps.length, hasRail],
  );

  // Restore finished-section progress on mount and whenever the storage key
  // settles (it gains the per-user suffix once /me resolves). Without this,
  // progress only loaded on the auto-start path — which returns early once
  // the tour has been seen, so a returning or relaunching user saw every
  // section unfinished again.
  useEffect(() => {
    try {
      const done = JSON.parse(localStorage.getItem(`${storageKey}:chapters`) || "[]");
      if (Array.isArray(done)) setDoneChapters(done.filter((d) => typeof d === "string"));
    } catch {
      /* ignore */
    }
  }, [storageKey]);

  // Auto-start until the tour is actually FINISHED or SKIPPED (the seen-mark
  // is written by dismiss/complete, not here) — someone who refreshed or
  // closed the tab mid-tour gets the walkthrough again from step 1 on their
  // next entry. Capped at 3 auto-launches so an uninterested prospect who
  // keeps ignoring it isn't nagged forever.
  useEffect(() => {
    let done = true;
    let attempts = 0;
    try {
      done = seenStore().getItem(storageKey) !== null;
      attempts = Number(seenStore().getItem(`${storageKey}:attempts`) || "0");
    } catch {
      done = true; // no storage -> never auto-start (avoid a nag loop)
    }
    if (done || attempts >= 3 || railPending) return;
    try {
      seenStore().setItem(`${storageKey}:attempts`, String(attempts + 1));
    } catch {
      /* ignore */
    }
    const timer = window.setTimeout(() => {
      setIdx(0);
      setIntroIdx(0);
      setView(startView());
      setActive(true);
    }, 700); // let the first paint settle so the anchors exist
    return () => window.clearTimeout(timer);
  }, [storageKey, seenStore, railPending, startView]);

  // Relaunch entry point (sidebar "Show me around" / help affordances).
  useEffect(() => {
    const onStart = () => {
      setIdx(0);
      setIntroIdx(0);
      setView(startView());
      setActive(true);
    };
    window.addEventListener(TOUR_EVENT, onStart);
    return () => window.removeEventListener(TOUR_EVENT, onStart);
  }, [startView]);

  // ONE GUIDE AT A TIME (./guideOverlays.ts). However the tour opened — auto,
  // the sidebar, the rail's closing button — it says so, and the first-run
  // rail closes instead of sitting over the targets this tour spotlights.
  // Presence is published too, so the ripple's "What if?" card stands aside
  // rather than stacking under the tour card. And if the rail opens (the
  // sidebar's "Replay the intro") the tour steps down, quietly: no seen-mark,
  // no skip beacon — the prospect did not skip it, they asked for the other.
  useEffect(() => {
    setGuideOpen("tour", active);
    if (active) announceGuideOpened("tour");
    return () => setGuideOpen("tour", false);
  }, [active]);
  useEffect(() => onGuideOpened("tour", () => setActive(false)), []);

  const dismiss = useCallback(() => {
    setActive(false);
    // A hierarchical tour is "finished" when every chapter is done, however
    // the prospect closes it from there (the menu's Done, Esc, anything).
    // completedRef alone only ever fired for LINEAR tours — a shape the
    // platform refuses — so every real completion was reporting tour_skip
    // and the ops funnel read zero tour_done forever.
    const finishedAllChapters =
      hierarchical && chapters.length > 0 && chapters.every((c) => doneChapters.includes(c));
    postDemoEvent(completedRef.current || finishedAllChapters ? "tour_done" : "tour_skip");
    completedRef.current = false;
    try {
      seenStore().setItem(storageKey, new Date().toISOString());
    } catch {
      /* ignore */
    }
  }, [storageKey, seenStore, hierarchical, chapters, doneChapters]);

  const finishChapter = useCallback(() => {
    if (!chapter) return;
    setDoneChapters((prev) => {
      const nextDone = prev.includes(chapter) ? prev : [...prev, chapter];
      try {
        localStorage.setItem(`${storageKey}:chapters`, JSON.stringify(nextDone));
      } catch {
        /* ignore */
      }
      return nextDone;
    });
    setChapter(null);
    setIdx(0);
    setView("menu");
  }, [chapter, storageKey]);

  const next = useCallback(() => {
    setIdx((i) => {
      if (i >= steps.length - 1) {
        if (hierarchical) {
          finishChapter();
          return 0;
        }
        completedRef.current = true; // finished, not skipped
        dismiss();
        return i;
      }
      return i + 1;
    });
  }, [steps.length, dismiss, hierarchical, finishChapter]);

  const back = useCallback(() => {
    if (hierarchical) {
      setIdx((i) => {
        if (i <= 0) {
          setChapter(null);
          setView("menu");
          return 0;
        }
        return i - 1;
      });
      return;
    }
    setIdx((i) => Math.max(0, i - 1));
  }, [hierarchical]);

  const openChapter = useCallback((name: string) => {
    setChapter(name);
    setIdx(0);
    setView("steps");
  }, []);

  // Keyboard: Esc skips, arrows navigate, Enter advances. Capture phase so
  // the app underneath never sees the keys while the tour is up.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        dismiss();
      } else if (view === "steps" && (e.key === "ArrowRight" || e.key === "Enter")) {
        e.preventDefault();
        e.stopPropagation();
        next();
      } else if (view === "steps" && e.key === "ArrowLeft") {
        e.preventDefault();
        e.stopPropagation();
        back();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [active, view, dismiss, next, back]);

  // Multi-page steps: on entering a step (forward, back, or dot-jump) whose
  // `path` is not the page the prospect is on, navigate there, then give the
  // target NAV_WAIT_MS to appear before the centered fallback stops saying
  // "Taking you there…".
  //
  // Deliberately NOT depending on the router's location: reading
  // window.location.pathname imperatively means exactly ONE navigation
  // attempt per step entry — if a route guard bounces us somewhere else, we
  // fall back to the centered card instead of re-navigating in a loop. And
  // dismissing/finishing the tour never navigates: the prospect keeps
  // exploring from wherever the tour left them.
  useEffect(() => {
    if (!active || view !== "steps") return;
    const current = steps[Math.min(idx, steps.length - 1)];
    if (!current) return;
    const raw = (current.path ?? "").trim();
    const path = resolveStepPath(raw);
    // EXACT pathname match for every path, token-resolved or literal. It
    // used to be a prefix match for literal paths, and every demo route
    // starts with /w/demo-<subdomain>: a "/w/demo-x" step never left the
    // landing canvas, and the bare prefix also matched the "--<key>" desks.
    const already = path ? isOnStepPath(window.location.pathname, path) : true;
    if (path && !already) {
      try {
        // history.push (not the typed navigate()): step paths are runtime
        // strings from the demo spec, unknowable to the route typegen.
        router.history.push(path);
      } catch {
        /* malformed path — stay put; the fallback card still shows the step */
      }
    }
    if (!current.target) {
      setWaitingForTarget(false);
      return;
    }
    if (measureTarget(current.target)) {
      setWaitingForTarget(false); // already on screen — no wait, no hint
      return;
    }
    // Only claim to be navigating when we actually did. A step whose path
    // is absent, unresolvable, or already satisfied goes nowhere, so
    // "Taking you there…" would be a lie the user watches time out —
    // observed live on an unresolvable "@workspace".
    const navigated = !!path && !already;
    if (!navigated) {
      setWaitingForTarget(false);
      return;
    }
    setWaitingForTarget(true);

    // SECOND HOP. A sentinel resolves off links that exist on the page we
    // are standing on, so from a surface that carries none — Plan renders
    // no "/w/" links at all — "@workspace" can only reach the workspace
    // DIRECTORY. The directory is not where the step's target lives
    // ([data-tour=canvases] is on /w/<slug>, the overview), so the tour
    // arrived somewhere real and still had nothing to point at. Once the
    // directory has rendered, the links it lists make the sentinel
    // resolvable for real: re-resolve ONCE and hop again. Exactly one
    // extra hop, so a route guard still cannot start a loop.
    const hop = window.setTimeout(() => {
      if (!current.target || measureTarget(current.target)) return;
      const again = resolveStepPath(raw);
      if (again && !isOnStepPath(window.location.pathname, again)) {
        try {
          router.history.push(again);
        } catch {
          /* malformed — the centered card still shows the step */
        }
      }
    }, 500);

    const timer = window.setTimeout(() => setWaitingForTarget(false), NAV_WAIT_MS);
    return () => {
      window.clearTimeout(hop);
      window.clearTimeout(timer);
    };
  }, [active, view, idx, steps, router]);

  const step = steps[Math.min(idx, steps.length - 1)];
  const inSteps = view === "steps";
  const [rect, remeasureTarget] = useTargetRect(
    active && inSteps ? step?.target : undefined,
    active && inSteps,
  );
  const targetFound = rect !== null;

  // The wait ends the moment the target lands (often well before the budget).
  useEffect(() => {
    if (targetFound) setWaitingForTarget(false);
  }, [targetFound]);

  // Bring the target into view when the step changes — and again when a
  // late target (post-navigation) first appears, which the step-change pass
  // necessarily missed. scrollIntoView walks every scroll container the
  // target sits in (the canvas "desk" ScrollArea, the agent rail), so we
  // never have to know which pane owns the scroll.
  //
  // Only `targetFound` (a boolean) and the step index gate this, never the
  // rect itself: the effect must not re-fire on the scroll it just caused.
  // A prospect who then scrolls away is left alone.
  useEffect(() => {
    if (!active || !inSteps || !targetFound) return;
    const selector = steps[Math.min(idx, steps.length - 1)]?.target;
    if (!selector) return;
    let el: Element | null = null;
    try {
      el = document.querySelector(selector);
    } catch {
      return; /* invalid selector — centered fallback handles it */
    }
    if (!el) return;
    // "nearest" is the right default — the minimum scroll, so spotlighting a
    // sidebar item never yanks the page. But it is defined as a NO-OP once
    // the element spans the whole scrollport, which is exactly the canvas
    // sheet: taller than the window, so it can never be framed. Show those
    // from the top edge instead, where the document actually begins.
    const tall = el.getBoundingClientRect().height > window.innerHeight;
    el.scrollIntoView({ block: tall ? "start" : "nearest", inline: "nearest" });
    // Re-measure once the scroll has settled. The rect the spotlight is
    // holding was taken BEFORE this scroll, so without this the first painted
    // frame of a step spotlights where the target used to be.
    const raf = window.requestAnimationFrame(remeasureTarget);
    return () => window.cancelAnimationFrame(raf);
  }, [active, inSteps, idx, steps, targetFound, remeasureTarget]);

  if (!active) return null;
  if (view === "steps" && !step) return null;
  const stepSafe: DemoTourStep = step ?? { title: "", body: "" };

  // What the spotlight paints: the target's rect clipped to the window. The
  // raw `rect` stays the source of truth for "does the target exist" (the
  // "Taking you there…" hint); `spot` is the geometry, and it goes null when
  // the target has scrolled fully out of view.
  const spot = visibleRect(rect);
  const last = idx >= steps.length - 1;
  const walkthroughLed =
    !hierarchical &&
    !(allSteps[0]?.target ?? "").trim() &&
    !(allSteps[0]?.path ?? "").trim();
  // A deliberate full-card screen (no anchor, no navigation) — the intro
  // screens of walkthrough-led demos. Rendered wider, with a larger title.
  const introCard = !(stepSafe.target ?? "").trim() && !(stepSafe.path ?? "").trim();
  const allDone = chapters.length > 0 && chapters.every((c) => doneChapters.includes(c));
  // The body reads long-form: a serif face, one size up, looser leading —
  // deliberately different from the app chrome so the tour reads like a
  // guide, not another panel. The face survives the move to dark (it IS the
  // guide's voice); only its colour changes. See ../chromeTheme.ts.
  const bodyType: React.CSSProperties = DEMO_SERIF_BODY;

  // Intro sequence — one or more full-card screens before the section menu.
  // Rendered as its own view (not a walk step) because these carry no anchor
  // and must not be reachable from a section, which is what made the welcome
  // text look like it reopened on every section.
  if (view === "intro" && introSteps.length > 0) {
    const st = introSteps[Math.min(introIdx, introSteps.length - 1)];
    if (!st) return null;
    const last = introIdx >= introSteps.length - 1;
    return createPortal(
      <div aria-hidden={false} style={demoChromeVars}>
        <div className="fixed inset-0 z-[140] bg-black/65" onClick={(e) => e.stopPropagation()} />
        <dialog
          open
          aria-modal="true"
          aria-label={personalize(st.title)}
          className={`fixed top-1/2 left-1/2 z-[141] m-0 w-[520px] max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 p-7 ${DEMO_PANEL}`}
        >
          <div className="flex items-center gap-2">
            <span className={DEMO_BADGE}>Demo guide</span>
            <span className={DEMO_EYEBROW}>Guided tour</span>
          </div>
          <div className="mt-2 text-[20px] leading-snug font-semibold">
            {personalize(st.title)}
          </div>
          <p className="mt-2.5" style={{ ...bodyType, whiteSpace: "pre-line" }}>
            {personalize(st.body)}
          </p>
          <div className="mt-5 flex items-center justify-between">
            <span className="text-[12px] text-[var(--demo-muted)]">
              {introSteps.length > 1 ? `${introIdx + 1} of ${introSteps.length}` : ""}
            </span>
            <span className="flex items-center gap-2">
              {introIdx > 0 && (
                <button
                  type="button"
                  className={DEMO_GHOST_BTN}
                  onClick={() => setIntroIdx((i) => Math.max(0, i - 1))}
                >
                  Back
                </button>
              )}
              <button type="button" className={DEMO_GHOST_BTN} onClick={dismiss}>
                Skip
              </button>
              <button
                type="button"
                className={DEMO_PRIMARY_BTN}
                onClick={() => (last ? setView("menu") : setIntroIdx((i) => i + 1))}
              >
                {st.next_label?.trim() || (last ? "See the sections" : "Next")}
              </button>
            </span>
          </div>
        </dialog>
      </div>,
      document.body,
    );
  }

  // The menu needs its OWN heading. It used to borrow `intro.title`, which
  // was harmless when there was one intro step but reads as stale state now
  // that a tour can open with several — the menu ended up titled "Why this
  // example?", the last intro screen's headline, on every return to it.
  const menuHeading = allDone
    ? "That is the whole tour"
    : doneChapters.length > 0
      ? "Pick up where you left off"
      : "Pick a section";

  if (view === "menu") {
    return createPortal(
      <div aria-hidden={false} style={demoChromeVars}>
        <div className="fixed inset-0 z-[140] bg-black/65" onClick={(e) => e.stopPropagation()} />
        <dialog
          open
          aria-modal="true"
          aria-label={menuHeading}
          className={`fixed top-1/2 left-1/2 z-[141] m-0 w-[440px] max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 p-6 ${DEMO_PANEL}`}
        >
          <div className="flex items-center gap-2">
            <span className={DEMO_BADGE}>Demo guide</span>
            <span className={DEMO_EYEBROW}>{allDone ? "Tour complete" : "Guided tour"}</span>
          </div>
          <div className="mt-2 text-[17px] leading-snug font-semibold">{menuHeading}</div>
          {/* The intro BODY deliberately does not repeat on every RETURN to the
              menu. Finishing a section comes back here, and re-rendering the
              full welcome text made it read as if it opened every section.
              ONE case wants it: a tour whose intro march was skipped because
              this demo has a first-run rail — there the demo-specific line
              would otherwise be lost entirely, so it shows once, on the first
              visit to the menu. (The old condition also tested
              `introSteps.length === 0`, which could never be true here:
              `intro` is null in exactly that case.) */}
          {allDone ? (
            <p className="mt-2" style={bodyType}>
              You saw all of it. Open any section again below, or close this and
              explore on your own. The tour stays available from &ldquo;Show me
              around&rdquo; in the sidebar.
            </p>
          ) : (
            hasRail &&
            intro?.body &&
            doneChapters.length === 0 && (
              <p className="mt-2" style={{ ...bodyType, whiteSpace: "pre-line" }}>
                {personalize(intro.body)}
              </p>
            )
          )}
          <div className="mt-4 flex flex-col gap-1.5">
            {chapters.map((name, i) => {
              const count = allSteps.filter((s) => (s.section ?? "").trim() === name).length;
              const done = doneChapters.includes(name);
              const upNext = !done && chapters.findIndex((c) => !doneChapters.includes(c)) === i;
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => openChapter(name)}
                  className={`flex items-center justify-between gap-3 rounded-lg border px-4 py-2.5 text-left transition-colors hover:border-[var(--demo-accent)] hover:bg-[#2f2a26] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--demo-ring)] ${
                    upNext
                      ? "border-[var(--demo-accent)] bg-[var(--demo-accent-soft)]"
                      : "border-[var(--demo-border)] bg-[var(--demo-raised)]"
                  }`}
                >
                  <span className="flex min-w-0 items-center gap-2.5">
                    <span
                      className={`text-[12px] font-semibold tabular-nums ${
                        // On dark, the app's success green (#2f7d4f) sits at
                        // 2.2:1 — unreadable. A light mint clears 8:1 and still
                        // reads as "done".
                        done ? "text-[#7ddba4]" : "text-[var(--demo-muted)]"
                      }`}
                    >
                      {done ? "✓" : i + 1}
                    </span>
                    <span className="truncate text-sm font-medium">{name}</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2 text-[12px] text-[var(--demo-muted)]">
                    {upNext && (
                      <span className="font-semibold text-[var(--demo-accent)]">Up next</span>
                    )}
                    {count} {count === 1 ? "step" : "steps"}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="mt-4 flex items-center justify-between">
            <span className="text-[12px] text-[var(--demo-muted)]">
              {allDone ? "All sections complete." : "Each section takes under a minute."}
            </span>
            <span className="flex items-center gap-2">
              <button type="button" className={DEMO_GHOST_BTN} onClick={dismiss}>
                {allDone ? "Done" : "Close"}
              </button>
              {!allDone && (
                <button
                  type="button"
                  className={DEMO_PRIMARY_BTN}
                  onClick={() => {
                    const nextUp = chapters.find((c) => !doneChapters.includes(c));
                    if (nextUp) openChapter(nextUp);
                  }}
                >
                  Continue
                </button>
              )}
            </span>
          </div>
        </dialog>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div aria-hidden={false} style={demoChromeVars}>
      {/* Click-catcher: blocks interaction with the app while the tour runs
          (the spotlight's shadow scrim doesn't intercept events by itself).

          THE SPOTLIGHT IS A WINDOW, NOT A PICTURE. When a step has a visible
          target, the catcher is four strips AROUND the spotlit box, so the
          one element the step is talking about stays genuinely clickable.
          Several authored steps literally instruct a click ("Click the
          number", "open the conversation picker"), and a full-viewport
          catcher made every one of them impossible to obey — hit-tested
          live: the click landed on this div. A prospect who clicks the
          spotlit thing and navigates mid-step is fine — the target
          re-measure loop already degrades a missing anchor to the centered
          card. With no visible target, the full catcher stands as before. */}
      {spot ? (
        <>
          <div
            className="fixed inset-x-0 top-0 z-[140]"
            style={{ height: Math.max(spot.top - SPOT_PAD, 0) }}
            onClick={(e) => e.stopPropagation()}
          />
          <div
            className="fixed inset-x-0 bottom-0 z-[140]"
            style={{ top: spot.top + spot.height + SPOT_PAD }}
            onClick={(e) => e.stopPropagation()}
          />
          <div
            className="fixed left-0 z-[140]"
            style={{
              top: Math.max(spot.top - SPOT_PAD, 0),
              height: spot.height + SPOT_PAD * 2,
              width: Math.max(spot.left - SPOT_PAD, 0),
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <div
            className="fixed right-0 z-[140]"
            style={{
              top: Math.max(spot.top - SPOT_PAD, 0),
              height: spot.height + SPOT_PAD * 2,
              left: spot.left + spot.width + SPOT_PAD,
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </>
      ) : (
        <div className="fixed inset-0 z-[140]" onClick={(e) => e.stopPropagation()} />
      )}

      {spot ? (
        // Spotlight: the box-shadow paints the dimmed scrim around the cutout.
        // The ring keeps the brand orange — it is drawn ON the app, marking the
        // app's own element, and it is the one place where narrator and product
        // deliberately touch.
        <div
          className="pointer-events-none fixed z-[140] rounded-[10px]"
          style={{
            top: spot.top - SPOT_PAD,
            left: spot.left - SPOT_PAD,
            width: spot.width + SPOT_PAD * 2,
            height: spot.height + SPOT_PAD * 2,
            boxShadow: `0 0 0 2px ${DEMO_CHROME.accent}, 0 0 0 9999px rgba(12, 10, 9, 0.62)`,
            transition: "top .18s ease, left .18s ease, width .18s ease, height .18s ease",
          }}
        />
      ) : (
        <div className="fixed inset-0 z-[140] bg-black/62" />
      )}

      {/* Native <dialog> (always rendered `open`, positioned by us — we skip
          showModal() so the card can sit next to the spotlight instead of
          being centered by the UA). m-0 cancels the UA's `margin: auto`. */}
      <dialog
        open
        ref={cardRef}
        aria-modal="true"
        aria-label={personalize(stepSafe.title)}
        // The dimmer behind this card is nearly the card's own value, so the
        // panel's brighter hairline (DEMO_PANEL) is what separates them.
        className={`fixed z-[141] m-0 max-w-[calc(100vw-32px)] ${DEMO_PANEL} ${
          introCard ? "w-[560px] p-7" : "w-[420px] p-5"
        }`}
        style={cardStyleFor(spot, cardH)}
      >
        <div className="flex items-center gap-2">
          <span className={DEMO_BADGE}>Demo guide</span>
          {!walkthroughLed && (
            <span className={DEMO_EYEBROW}>
              {hierarchical && chapter ? `${chapter} · ` : ""}
              Step {idx + 1} of {steps.length}
            </span>
          )}
        </div>
        {/* Navigation-in-flight hint: the step pointed somewhere that isn't on
            screen yet (usually because we just changed pages). Shown only
            during the wait budget; afterwards the centered card stands alone. */}
        {waitingForTarget && !rect && (
          <div className="mt-1.5 flex items-center gap-1.5 text-[11px] font-medium text-[var(--demo-muted)]">
            <span className="size-1.5 animate-pulse rounded-full bg-[var(--demo-accent)]" />
            Taking you there…
          </div>
        )}
        <div
          className={`mt-2 leading-snug font-semibold ${introCard ? "text-[20px]" : "text-[16px]"}`}
        >
          {personalize(stepSafe.title)}
        </div>
        <div className="mt-2 flex flex-col gap-2.5" style={bodyType}>
          {personalize(stepSafe.body)
            .split(/\n{2,}/)
            .map((para, i) => (
              <p key={i} style={{ whiteSpace: "pre-line" }}>
                {para}
              </p>
            ))}
        </div>

        <div className="mt-3 flex items-center gap-1.5">
          {steps.map((s, i) => (
            <button
              key={`${i}-${s.title}`}
              type="button"
              aria-label={`Go to step ${i + 1}`}
              onClick={() => setIdx(i)}
              className={`size-1.5 rounded-full transition-colors ${
                i === idx
                  ? "bg-[var(--demo-accent)]"
                  : "bg-[var(--demo-border)] hover:bg-[var(--demo-muted)]"
              }`}
            />
          ))}
        </div>

        <div className="mt-3.5 flex items-center justify-between gap-2">
          {hierarchical || !last ? (
            <button
              type="button"
              onClick={
                hierarchical
                  ? () => {
                      setChapter(null);
                      setIdx(0);
                      setView("menu");
                    }
                  : dismiss
              }
              className={DEMO_SUBTLE_BTN}
            >
              {hierarchical ? "All sections" : "Skip tour"}
            </button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <button type="button" className={DEMO_GHOST_BTN} disabled={idx === 0} onClick={back}>
              Back
            </button>
            {/* No autoFocus needed: the window-level keydown handler already
                makes Enter/arrows work regardless of focus. */}
            <button type="button" className={DEMO_PRIMARY_BTN} onClick={next}>
              {(stepSafe.next_label ?? "").trim() ||
                (last ? (hierarchical ? "Finish section" : "Done") : "Next")}
            </button>
          </div>
        </div>
      </dialog>
    </div>,
    document.body,
  );
}

/** Sidebar-footer relaunch affordance — render only for demo sessions.
 *
 *  "Take the tour" was read as "start the demo over from the beginning", which
 *  is what the first-run rail does, not this. "Show me around" says what the
 *  tour actually is now that it opens on its chapter menu: a guide to the
 *  product you are already standing in. */
export function DemoTourLauncher({ collapsed }: { collapsed?: boolean }) {
  return (
    <button
      type="button"
      onClick={startDemoTour}
      title="Show me around"
      aria-label="Show me around"
      className={
        collapsed
          ? "mx-auto flex size-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          : "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[12.5px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
      }
    >
      <CircledQuestionIcon />
      {!collapsed && <span>Show me around</span>}
    </button>
  );
}

function CircledQuestionIcon() {
  // Inline (lucide HelpCircle geometry) so this file stays dependency-light.
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </svg>
  );
}
